import React, { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LLMChoices } from "@/types";
import { RiArrowDownSLine, RiChatSmileAiLine } from "@remixicon/react";
import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuItem,
  SqDropdownMenuPortal,
  SqDropdownMenuTrigger,
} from "@/components/squircle-ui/dropdown";

interface ModelOption {
  id: LLMChoices;
  title: string;
  icon: React.ReactNode;
}

interface ModelSelectorDropdownProps {
  selectedModelId: LLMChoices | null;
  onModelChange: (modelId: LLMChoices) => void;
}

const ModelSelectorDropdown: React.FC<ModelSelectorDropdownProps> = ({
  selectedModelId,
  onModelChange,
}) => {
  const modelOptions: ModelOption[] = useMemo(
    () => [
      {
        id: LLMChoices.quality,
        title: "Quality",
        icon: null,
      },
      {
        id: LLMChoices.fast,
        title: "Fast",
        icon: null,
      },
    ],
    [],
  );

  useEffect(() => {
    if (!selectedModelId) {
      onModelChange(modelOptions[0].id);
    }
  }, [selectedModelId, onModelChange, modelOptions]);

  return (
    <SqDropdownMenu>
      <SqDropdownMenuTrigger asChild>
        <Button variant="outline" className="max-w-[190px] h-8 border-0">
          <RiChatSmileAiLine size={14} color="#9AA3BD" className="shrink-0" />
          <span className="truncate text-[#021859CC] text-[12px]">
            {modelOptions.find((option) => option.id === selectedModelId)?.title ||
              "Quality"}
          </span>
          <RiArrowDownSLine size={14} className="shrink-0" color="#9aa3bd" />
        </Button>
      </SqDropdownMenuTrigger>
      <SqDropdownMenuPortal>
        <SqDropdownMenuContent
          radius={8}
          align="start"
          className="w-[190px] p-0 bg-white shadow-none mb-1 rounded-[8px] border-border-gray"
        >
          <div className="h-[32px] flex items-center text-[10px] font-medium text-brand-primary-600 px-[9px] py-[6px] tracking-[1px] uppercase leading-[12px]">
            Response
          </div>
          {modelOptions.map((option, index) => (
            <SqDropdownMenuItem
              key={option.id}
              radius={0}
              className={cn(
                "cursor-pointer group h-[32px] flex items-center pl-[9px] py-[6px] pr-[8px] rounded-none !bg-transparent mx-[1px] w-[99%]",
                selectedModelId === option.id
                  ? "squircle-fill-selected-gray hover:squircle-fill-selected-gray text-brand-primary-800 hover:text-brand-primary-800"
                  : "hover:squircle-fill-hover-gray text-brand-primary-new/70 hover:text-brand-primary-new/70",
                modelOptions.length - 1 === index &&
                  "squircle-radius-bottom-left-[8px] squircle-radius-bottom-right-[8px]",
              )}
              onClick={() => onModelChange(option.id)}
            >
              {option.icon && <span className="shrink-0 mr-1">{option.icon}</span>}
              <span className="truncate leading-[20px] text-[12px] font-normal">
                {option.title}
              </span>
            </SqDropdownMenuItem>
          ))}
        </SqDropdownMenuContent>
      </SqDropdownMenuPortal>
    </SqDropdownMenu>
  );
};

export default ModelSelectorDropdown;
